/**
 * Actiekaarten spelen.
 *
 * Een actiekaart draagt zijn doelwit mee in de zet zelf (welk landschap, welke
 * handkaart), in plaats van een wachtvraag te openen. Dat scheelt een heen-en-weer en
 * werkt voor het scherm net zo goed als voor de AI: beide berekenen eerst welke
 * doelwitten geldig zijn en sturen er één mee.
 *
 * Verweer gaat wél automatisch: heeft de tegenstander de juiste tegenkaart in zijn hand,
 * dan wordt die ingezet. Dat is geen keuze in het spel — een Bisschop tegen een Rooftocht
 * spelen is altijd in je voordeel.
 */
import { LANDSCAPE_RESOURCE, type Seat } from '@catan/contracts';
import {
  counterInHand,
  COUNTER_CARDS,
  destructibleBuildings,
  knightsOf,
  rollAttack,
  type ActionCardName,
} from './actioncards.js';
import { naarEigenLandschap } from './events.js';
import { takeBackCard } from './eventcards.js';
import type { GameState, PlayerState, Side } from './state.js';
import { opponentOf } from './state.js';

/** Waar een actiekaart zich op richt. Niet elke kaart heeft een doelwit nodig. */
export type CardTarget =
  /** Landschappen bij jezelf of bij je tegenstander. */
  | { readonly kind: 'landschappen'; readonly tiles: readonly { column: number; side: Side }[] }
  /** Een gebouwde kaart bij je tegenstander. */
  | { readonly kind: 'gebouw'; readonly column: number; readonly side: Side }
  /** Een handkaart van je tegenstander, op plaats `index`. */
  | { readonly kind: 'handkaart'; readonly index: number }
  /** Het gewenste resultaat van de opbrengstendobbelsteen. */
  | { readonly kind: 'worp'; readonly value: number };

export interface PlayResult {
  readonly state: GameState;
  readonly log: readonly string[];
}

/**
 * Haalt één exemplaar van een kaart uit een hand.
 *
 * Met `filter` zou je álle exemplaren weggooien, en van de Bisschop en de Kruidenheks
 * zitten er twee in het spel — dan verdwijnt er stilletjes een kaart uit het spel.
 */
function removeOne(hand: readonly number[], cardId: number): number[] {
  const kopie = [...hand];
  const index = kopie.indexOf(cardId);
  if (index !== -1) kopie.splice(index, 1);
  return kopie;
}

/**
 * Valt er bij deze speler iets te halen dat de dief ook kwíjt kan?
 *
 * "Grondstoffen kunnen alleen geroofd worden, als je ze op je landen van dat type kunt
 * plaatsen" (handboek p. 11 §5, en p. 12 §9 voor de Handelaar). Is het antwoord nee, dan
 * heeft de vraag geen enkel geldig antwoord — en dan hoort hij niet gesteld te worden.
 */
export function heeftPlaatsbareBuit(dief: PlayerState, slachtoffer: PlayerState): boolean {
  return slachtoffer.principality.landscapes.some(
    (t) =>
      t.resources > 0 && naarEigenLandschap(dief, LANDSCAPE_RESOURCE[t.landscape]) !== undefined,
  );
}

/**
 * Speelt een actiekaart. De kaart is op dit punt al gecontroleerd en uit de hand gehaald.
 */
export function playActionCard(
  state: GameState,
  seat: Seat,
  naam: ActionCardName,
  target: CardTarget | undefined,
): PlayResult {
  const players = [...state.players] as [PlayerState, PlayerState];
  const tegen = opponentOf(seat);
  const log: string[] = [`${players[seat].name} speelt ${naam}`];
  switch (naam) {
    case 'Alchimist': {
      // "Speel deze kaart voordat je met de dobbelsteen gooit — en je bepaalt zelf het
      // resultaat van de opbrengstendobbelsteen."
      if (target?.kind !== 'worp') throw new Error('Alchimist heeft een gewenste worp nodig');
      // Een dobbelsteen heeft zes vlakken. Zonder deze controle ging elk getal er via een
      // ongecontroleerde `as`-cast in — ook een 0 of een 99 — en dan brengt geen enkel
      // landschap meer op, of juist eentje dat niet bestaat.
      if (!Number.isInteger(target.value) || target.value < 1 || target.value > 6) {
        throw new Error('de Alchimist kiest een getal van 1 tot en met 6');
      }
      log.push(`de opbrengstendobbelsteen wordt ${target.value}`);
      return { state: { ...state, forcedProduction: target.value as 1 | 2 | 3 | 4 | 5 | 6 }, log };
    }

    case 'Karavaan': {
      // "Verwijder 2 van je grondstoffen en neem er 2 **van jouw keuze** voor terug"
      // (p. 12 §10, met als voorbeeld: "je draait je bos van 3 hout naar 1 hout terug en
      // je berg 1 erts en je akker 1 graan omhoog"). In de woorden van Thijs: een
      // banktransactie met jezelf — twee afgeven naar keuze, twee terugnemen naar keuze,
      // ook dezelfde soort, en de andere speler blijft er helemaal buiten. Beide helften
      // zijn dus vragen aan de speler in plaats van loten — het loten wás de klacht. De
      // enige grens is de plaatsingsregel die overal geldt: een vol landschap kan er
      // niets bij.
      return {
        state: { ...state, pending: { kind: 'karavaan', seat, stap: 'geef', resterend: 2 } },
        log,
      };
    }

    case 'Handelaar': {
      // "Neem je tegenstander 2 grondstoffen van je keuze af en geef hem daarvoor een
      // willekeurige grondstof terug." Die keuze is aan de speler, dus we zetten de vraag
      // klaar in plaats van er zelf twee aan te wijzen.
      //
      // Wél eerst kijken of er íéts te halen valt dat je ook kwijt kunt (handboek p. 12 §9).
      // Zonder die controle ging de vraag open met nul geldige antwoorden, en stond het
      // potje stil op een keuze die niemand kon maken.
      if (!heeftPlaatsbareBuit(players[seat], players[tegen])) {
        log.push('je tegenstander heeft niets dat jij kwijt kunt');
        return { state, log };
      }
      return {
        state: { ...state, pending: { kind: 'handelaar', seat, from: tegen, resterend: 2 } },
        log,
      };
    }

    case 'Spion': {
      // "Kies uit de handkaarten van je tegenstander 1 kaart uit." Ook hier is de keuze
      // aan de speler; zolang de vraag openstaat mag hij die hand zien.
      if (players[tegen].hand.length === 0) {
        log.push('je tegenstander heeft geen handkaarten');
        return { state, log };
      }
      return { state: { ...state, pending: { kind: 'spion', seat, from: tegen } }, log };
    }

    case 'Rooftocht':
    case 'Vuurduivel':
    case 'Zwarte ridder': {
      // Alle drie de aanvallen lopen hetzelfde: eerst krijgt de verdediger de kans zijn
      // tegenkaart in te zetten, en pas daarna rolt de dobbelsteen. Heeft hij die kaart
      // niet, dan gaat het in één keer door.
      const verweer = counterInHand(players[tegen], naam);
      if (verweer !== undefined) {
        return {
          state: {
            ...state,
            pending: {
              kind: 'kies-tegenkaart',
              seat: tegen,
              attacker: seat,
              attack: naam,
              counterId: verweer,
              target,
            },
          },
          log: [...log, `${players[tegen].name} kan zich verweren`],
        };
      }
      return beslechtAanval({ ...state, players }, seat, naam, target, false, log);
    }

    case 'Bisschop':
    case 'Kruidenheks': {
      // Deze kaarten bestaan alleen als verweer; ze worden automatisch ingezet zodra de
      // bijbehorende aanval komt, dus uit zichzelf spelen doen ze niets.
      log.push('deze kaart werkt alleen als verweer');
      return { state, log };
    }

    case 'Verkenner': {
      // De Verkenner speel je niet uit je hand: hij hoort bij het stichten van een dorp,
      // en het spel vraagt er dan zelf om (handboek p. 46). `isLegalAction` houdt hem
      // daarom tegen; dit geval bestaat alleen zodat de opsomming volledig blijft.
      log.push('de Verkenner zet je in bij het bouwen van een dorp');
      return { state, log };
    }
  }
}

/**
 * De verslagregel van een aanvalsworp.
 *
 * Eén plek die de vorm bepaalt, zodat de client hem terug kan lezen zonder te gokken. De
 * regel gaat naar béíde spelers — het scherm laat er de dobbelsteen op rollen, want tot nu
 * toe zag je alleen de uitkomst en nooit de worp.
 */
export const aanvalsworpRegel = (worp: number, staart: string): string => `worp ${worp} — ${staart}`;

/**
 * Het worpgetal uit een reeks verslagregels, als er een aanval in zat.
 *
 * Hoort bewust naast `aanvalsworpRegel`: producent en lezer op één plek, met een toets die
 * ze aan elkaar vastpint. Anders staat er over een half jaar een reguliere expressie in de
 * webapp die niemand meer durft aan te raken.
 */
export function aanvalsworpUit(regels: readonly string[]): number | undefined {
  for (const regel of regels) {
    const treffer = /^worp ([1-6]) — /.exec(regel);
    if (treffer !== null) return Number(treffer[1]);
  }
  return undefined;
}

/**
 * De aanval zelf: de worp en wat hij aanricht.
 *
 * Los van `playActionCard`, want hij wordt op twee momenten aangeroepen — meteen als de
 * verdediger geen tegenkaart heeft, en anders pas nadat die heeft geantwoord.
 */
export function beslechtAanval(
  state: GameState,
  aanvaller: Seat,
  naam: ActionCardName,
  target: CardTarget | undefined,
  metVerweer: boolean,
  eerder: readonly string[] = [],
): PlayResult {
  const players = [...state.players] as [PlayerState, PlayerState];
  const tegen = opponentOf(aanvaller);
  const log = [...eerder];
  const afgelegd: number[] = [];
  let rng = state.rng;

  if (metVerweer) {
    const kaart = counterInHand(players[tegen], naam);
    if (kaart !== undefined) {
      players[tegen] = { ...players[tegen], hand: removeOne(players[tegen].hand, kaart) };
      afgelegd.push(kaart);
      log.push(`${players[tegen].name} weert af met de ${COUNTER_CARDS[naam] ?? 'tegenkaart'}`);
    }
  }

  const worp = rollAttack(rng, metVerweer);
  rng = worp.rng;
  const slachtoffer = worp.result.success ? tegen : aanvaller;

  if (naam === 'Rooftocht') {
    // "Bij 1-5 mag je **naar believen** 2 grondstoffen stelen. Bij 6 steelt hij van jou
    // 2 grondstoffen **naar keuze**" (p. 11 §5). In beide richtingen kíést de dief dus —
    // hier lootte de code. De keuze loopt via `kies-buit` → `plaats-buit`, dezelfde keten
    // als het handelsvoordeel, met een teller voor de tweede diefstal.
    const dief: Seat = worp.result.success ? aanvaller : tegen;
    const bestolene: Seat = worp.result.success ? tegen : aanvaller;
    log.push(aanvalsworpRegel(worp.result.roll, `${players[dief].name} steelt`));
    if (!heeftPlaatsbareBuit(players[dief], players[bestolene])) {
      log.push('er valt niets te halen dat de dief kwijt kan');
      return {
        state: {
          ...state,
          players,
          rng,
          pending: undefined,
          discard: [...state.discard, ...afgelegd],
        },
        log,
      };
    }
    return {
      state: {
        ...state,
        players,
        rng,
        pending: { kind: 'kies-buit', seat: dief, from: bestolene, resterend: 2 },
        discard: [...state.discard, ...afgelegd],
      },
      log,
    };
  }

  const kandidaten =
    naam === 'Vuurduivel'
      ? destructibleBuildings(players[slachtoffer])
      : knightsOf(players[slachtoffer]);
  if (kandidaten.length === 0) {
    log.push(aanvalsworpRegel(worp.result.roll, 'er valt niets te raken'));
    return {
      state: { ...state, players, rng, pending: undefined, discard: [...state.discard, ...afgelegd] },
      log,
    };
  }

  // Keert de aanval zich om, dan kiest de vérdediger wat de aanvaller terugneemt. Zo staat
  // het op de kaarten: "Bij 6 neem jij de ridder van zijn keuze terug" (Zwarte ridder) en
  // "Bij 6 neem jij het gebouw van zijn keuze terug" (Vuurduivel). Hier lootte de code, en
  // dan is de kaart niet meer dan een dobbelsteen.
  //
  // `kies-terugname` bestaat al voor de Burgeroorlog en past precies: `seat` is wie kiest,
  // `from` van wie de kaart is. Het scherm kent die vraag dus ook al.
  if (!worp.result.success) {
    log.push(aanvalsworpRegel(worp.result.roll, `${players[tegen].name} kiest wat er teruggaat`));
    return {
      state: {
        ...state,
        players,
        rng,
        pending: {
          kind: 'kies-terugname',
          seat: tegen,
          from: aanvaller,
          soort: naam === 'Vuurduivel' ? 'gebouw' : 'ridder-of-vloot',
        },
        discard: [...state.discard, ...afgelegd],
      },
      log,
    };
  }

  // Slaagt de aanval, dan kiest de aanvaller — die gaf zijn doelwit al mee in de zet.
  let doelwit = kandidaten[0]!;
  if (target?.kind === 'gebouw') {
    const gekozen = kandidaten.find((k) => k.column === target.column && k.side === target.side);
    if (gekozen !== undefined) doelwit = gekozen;
  }
  players[slachtoffer] = takeBackCard(
    players[slachtoffer],
    doelwit.column,
    doelwit.side,
    doelwit.cardId,
  );
  log.push(aanvalsworpRegel(worp.result.roll, `${players[slachtoffer].name} neemt een kaart terug`));
  return {
    state: { ...state, players, rng, pending: undefined, discard: [...state.discard, ...afgelegd] },
    log,
  };
}
